// 설정 > 보안 — 블랙/화이트리스트, 실패 임계값, 텔레그램, 패스키.

import { useEffect, useState } from 'react';
import {
  useAddBlacklist,
  useAddWhitelist,
  useAdminSettings,
  useBlacklist,
  useRemoveBlacklist,
  useRemoveWhitelist,
  useUpdateAdminSettings,
  useWhitelist,
} from '../hooks/useAdmin';
import { useTelegramConfig, useTestTelegram, useUpdateTelegram } from '../hooks/useTelegram';
import { useAddTrustedIp, useRemoveTrustedIp, useTrustedIps } from '../hooks/useTrustedIps';
import { Spinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { isApiError, formatApiError } from '../api/client';
import { PasskeysSection } from '../components/PasskeysSection';

export default function SettingsSecurity() {
  const blacklistQ = useBlacklist();
  const whitelistQ = useWhitelist();
  const settingsQ = useAdminSettings();
  const addBl = useAddBlacklist();
  const removeBl = useRemoveBlacklist();
  const addWl = useAddWhitelist();
  const removeWl = useRemoveWhitelist();
  const updateSettings = useUpdateAdminSettings();
  const toast = useToast();

  const [blIp, setBlIp] = useState('');
  const [blReason, setBlReason] = useState('');
  const [wlIp, setWlIp] = useState('');
  const [wlDesc, setWlDesc] = useState('');
  const [threshold, setThreshold] = useState<number>(5);
  const [whitelistEnabled, setWhitelistEnabled] = useState(false);

  useEffect(() => {
    if (settingsQ.data) {
      setThreshold(settingsQ.data.loginFailThreshold);
      setWhitelistEnabled(settingsQ.data.whitelistEnabled);
    }
  }, [settingsQ.data]);

  if (blacklistQ.isLoading || whitelistQ.isLoading || settingsQ.isLoading) return <Spinner />;

  const onAddBl = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addBl.mutateAsync({ ip: blIp.trim(), reason: blReason || undefined });
      setBlIp(''); setBlReason('');
      toast.success('블랙리스트 추가됨');
    } catch (err) {
      const msg = isApiError(err) && err.error === 'cannot_self_block'
        ? '자기 IP는 차단할 수 없습니다'
        : formatApiError(err, '추가 실패');
      toast.error(msg);
    }
  };

  const onAddWl = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addWl.mutateAsync({ ip: wlIp.trim(), description: wlDesc || undefined });
      setWlIp(''); setWlDesc('');
      toast.success('화이트리스트 추가됨');
    } catch (err) {
      toast.error(formatApiError(err, '추가 실패'));
    }
  };

  const onSaveThreshold = async () => {
    try {
      await updateSettings.mutateAsync({ loginFailThreshold: threshold });
      toast.success('임계값 저장됨');
    } catch (err) {
      toast.error(formatApiError(err, '저장 실패'));
    }
  };

  const onToggleWhitelist = async (next: boolean) => {
    try {
      await updateSettings.mutateAsync({ whitelistEnabled: next });
      setWhitelistEnabled(next);
      toast.success(next ? '화이트리스트 모드 ON' : '화이트리스트 모드 OFF');
    } catch (err) {
      const msg = isApiError(err) && err.error === 'current_ip_not_in_whitelist'
        ? '현재 IP를 먼저 화이트리스트에 추가해야 합니다'
        : formatApiError(err, '변경 실패');
      toast.error(msg);
    }
  };

  return (
    <div className="stack">
      <h2>설정 · 보안</h2>

      {/* 복구 코드 상시 경고 배너 — 보안 화면 진입 시 항상 노출 */}
      <div className="recovery-code-banner">
        <span className="recovery-code-banner__icon">&#9888;</span>
        <span>
          복구 코드 분실 시 데이터 복구가 불가합니다.
          복구 코드를 종이로 인쇄하거나 물리 금고에 보관했는지 점검하세요.
        </span>
      </div>

      <section className="card stack">
        <h3>로그인 실패 임계값</h3>
        <p className="muted">이 횟수만큼 실패하면 IP가 자동으로 블랙리스트에 등록됩니다.</p>
        <div className="row">
          <input type="number" min={1} max={100} value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))} style={{ width: 100 }} />
          <button type="button" className="btn btn--primary" onClick={onSaveThreshold} disabled={updateSettings.isPending}>저장</button>
        </div>
      </section>

      <section className="card stack">
        <h3>화이트리스트 모드</h3>
        <label className="checkbox">
          <input type="checkbox" checked={whitelistEnabled} onChange={(e) => onToggleWhitelist(e.target.checked)} />
          <span>화이트리스트에 등록된 IP만 접속 허용</span>
        </label>
        <p className="muted">활성화 전, 본인 현재 IP를 먼저 화이트리스트에 추가하세요.</p>
      </section>

      <section className="card stack">
        <h3>블랙리스트</h3>
        <form onSubmit={onAddBl} className="row">
          <input value={blIp} onChange={(e) => setBlIp(e.target.value)} placeholder="IP 주소" required />
          <input value={blReason} onChange={(e) => setBlReason(e.target.value)} placeholder="사유 (선택)" />
          <button type="submit" className="btn btn--primary">추가</button>
        </form>
        <ul className="ip-list">
          {blacklistQ.data?.map((b) => (
            <li key={b.ip}>
              <code>{b.ip}</code>
              {b.isAuto && <span className="badge">자동</span>}
              <span className="muted">{b.reason ?? ''}</span>
              <button type="button" className="btn btn--tiny danger"
                onClick={async () => { await removeBl.mutateAsync(b.ip); toast.info('해제됨'); }}>×</button>
            </li>
          ))}
          {blacklistQ.data?.length === 0 && <li className="muted">등록된 항목 없음</li>}
        </ul>
      </section>

      <section className="card stack">
        <h3>화이트리스트</h3>
        <form onSubmit={onAddWl} className="row">
          <input value={wlIp} onChange={(e) => setWlIp(e.target.value)} placeholder="IP 주소" required />
          <input value={wlDesc} onChange={(e) => setWlDesc(e.target.value)} placeholder="설명 (선택)" />
          <button type="submit" className="btn btn--primary">추가</button>
        </form>
        <ul className="ip-list">
          {whitelistQ.data?.map((w) => (
            <li key={w.ip}>
              <code>{w.ip}</code>
              <span className="muted">{w.description ?? ''}</span>
              <button type="button" className="btn btn--tiny danger"
                onClick={async () => { await removeWl.mutateAsync(w.ip); toast.info('해제됨'); }}>×</button>
            </li>
          ))}
          {whitelistQ.data?.length === 0 && <li className="muted">등록된 항목 없음</li>}
        </ul>
      </section>

      <TrustedIpsSection />
      <TelegramSection />
      <PasskeysSection />
    </div>
  );
}

// ─── 텔레그램 (실제 API 연동) ────────────────────────

function TelegramSection() {
  const toast = useToast();
  const cfgQ = useTelegramConfig();
  const update = useUpdateTelegram();
  const test = useTestTelegram();
  const [botToken, setBotToken] = useState(''); // 비어 있으면 변경 안 함
  const [chatId, setChatId] = useState('');

  // 서버에서 받아온 chatId로 초기 채움. botToken은 서버가 노출하지 않으므로 비어 있는 상태 유지.
  useEffect(() => {
    if (cfgQ.data) setChatId(cfgQ.data.chatId ?? '');
  }, [cfgQ.data]);

  if (cfgQ.isLoading) return <section className="card"><Spinner /></section>;
  const cfg = cfgQ.data;
  if (!cfg) return null;

  const handleError = (err: unknown, fallback: string) => {
    if (isApiError(err)) {
      if (err.error === 'telegram_not_configured') {
        toast.error('Bot Token과 Chat ID를 먼저 저장해야 활성화할 수 있습니다');
        return;
      }
      if (err.error === 'telegram_send_failed') {
        const detail = (err.body as { detail?: string } | null)?.detail ?? '';
        toast.error(`발송 실패 — ${detail}`);
        return;
      }
      toast.error(err.error);
      return;
    }
    toast.error(fallback);
  };

  const onSave = async () => {
    // botToken이 비어 있으면 변경하지 않음(서버에 undefined로 보냄). 명시적으로 비우려면 별도 X 버튼.
    const body = {
      ...(botToken !== '' ? { botToken } : {}),
      chatId,
    };
    try {
      await update.mutateAsync(body);
      setBotToken(''); // 저장 후 입력란 비움 — 다시 표시되지 않게
      toast.success('저장되었습니다');
    } catch (err) {
      handleError(err, '저장 실패');
    }
  };

  const onClearToken = async () => {
    if (!window.confirm('Bot Token을 삭제하면 활성화도 자동으로 해제됩니다. 계속하시겠어요?')) return;
    try {
      await update.mutateAsync({ botToken: null });
      toast.info('Bot Token 삭제됨');
    } catch (err) {
      handleError(err, '삭제 실패');
    }
  };

  const onToggleEnabled = async (next: boolean) => {
    try {
      await update.mutateAsync({ enabled: next });
      toast.success(next ? '텔레그램 활성화됨' : '텔레그램 비활성화됨');
    } catch (err) {
      handleError(err, '변경 실패');
    }
  };

  const onTest = async () => {
    // 입력 중인 값이 있으면 그것을 우선 사용 — 저장 전 미리 테스트 가능
    const body: { botToken?: string; chatId?: string } = {};
    if (botToken !== '') body.botToken = botToken;
    if (chatId !== '' && chatId !== cfg.chatId) body.chatId = chatId;
    try {
      await test.mutateAsync(body);
      toast.success('테스트 메시지를 보냈습니다');
    } catch (err) {
      handleError(err, '테스트 실패');
    }
  };

  return (
    <section className="card stack">
      <h3>텔레그램 알림</h3>
      <p className="muted">
        bot 정보를 저장하고 <b>활성화 토글</b>을 ON 해야 실제 알림이 발송됩니다. 저장만으로는 발송되지 않습니다.
      </p>
      <label><span>Bot Token {cfg.hasBotToken && <small className="muted">(저장됨 — 변경하려면 새 값 입력)</small>}</span>
        <div className="row">
          <input
            type="password"
            value={botToken}
            onChange={(e) => setBotToken(e.target.value)}
            placeholder={cfg.hasBotToken ? '••••••••• (변경하지 않음)' : '123456:ABC...'}
          />
          {cfg.hasBotToken && (
            <button type="button" className="btn btn--ghost danger" onClick={onClearToken}>토큰 삭제</button>
          )}
        </div>
      </label>
      <label><span>Chat ID</span>
        <input value={chatId} onChange={(e) => setChatId(e.target.value)} placeholder="-100... 또는 개인 chat id" />
      </label>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={cfg.enabled}
          onChange={(e) => onToggleEnabled(e.target.checked)}
          disabled={update.isPending}
        />
        <span>활성화 (이 토글이 ON일 때만 실제 발송)</span>
      </label>
      <div className="row">
        <button type="button" className="btn btn--primary" onClick={onSave} disabled={update.isPending}>
          {update.isPending ? '저장 중…' : '저장'}
        </button>
        <button type="button" className="btn btn--ghost" onClick={onTest} disabled={test.isPending}>
          {test.isPending ? '전송 중…' : '연결 테스트'}
        </button>
      </div>
    </section>
  );
}

// ─── 신뢰 IP 섹션 (TOTP 생략) ────────────────────────

function TrustedIpsSection() {
  const toast = useToast();
  const q = useTrustedIps();
  const add = useAddTrustedIp();
  const remove = useRemoveTrustedIp();
  const [manualIp, setManualIp] = useState('');
  const [manualDesc, setManualDesc] = useState('');

  if (q.isLoading) return <section className="card"><Spinner /></section>;
  const items = q.data?.items ?? [];
  const myIp = q.data?.myIp ?? null;
  // 서버가 직접 확인한 "현재 내 IP가 신뢰 목록에 있는지" — TOTP 생략이 실제 동작할지 진단용.
  const alreadyTrusted = q.data?.myIpIsTrusted ?? false;

  const onAdd = async (ip: string, description?: string) => {
    try {
      await add.mutateAsync({ ip: ip.trim(), description });
      toast.success('신뢰 IP 추가됨');
      setManualIp('');
      setManualDesc('');
    } catch (err) {
      toast.error(formatApiError(err, '추가 실패'));
    }
  };

  const onRemove = async (ip: string) => {
    try {
      await remove.mutateAsync(ip);
      toast.info('신뢰 IP 해제됨');
    } catch (err) {
      toast.error(formatApiError(err, '삭제 실패'));
    }
  };

  return (
    <section className="card stack">
      <h3>신뢰 IP (TOTP 생략)</h3>
      <p className="muted">
        등록된 IP에서 로그인할 때 TOTP 인증을 건너뜁니다 (마스터 패스워드만 요구).
        <b style={{ color: 'var(--warn)' }}> 공용 IP나 공용 기기에서는 등록하지 마세요.</b>
      </p>

      {myIp && (
        <div className="row" style={{ background: 'var(--bg)', padding: '10px', borderRadius: 'var(--radius)' }}>
          <span>현재 내 IP: <code>{myIp}</code></span>
          {alreadyTrusted ? (
            <span className="badge" style={{ background: 'var(--ok)', color: '#fff' }}>
              ✓ 신뢰됨 (다음 로그인부터 TOTP 생략)
            </span>
          ) : (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => onAdd(myIp, '현재 내 IP')}
              disabled={add.isPending}
            >
              이 IP 추가
            </button>
          )}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!manualIp.trim()) return;
          void onAdd(manualIp, manualDesc || undefined);
        }}
        className="row"
      >
        <input
          value={manualIp}
          onChange={(e) => setManualIp(e.target.value)}
          placeholder="IP 주소 (예: 192.168.1.10)"
          required
        />
        <input
          value={manualDesc}
          onChange={(e) => setManualDesc(e.target.value)}
          placeholder="설명 (선택)"
        />
        <button type="submit" className="btn btn--primary" disabled={add.isPending}>추가</button>
      </form>

      <ul className="ip-list">
        {items.length === 0 ? (
          <li className="muted">등록된 신뢰 IP 없음</li>
        ) : (
          items.map((t) => (
            <li key={t.ip}>
              <code>{t.ip}</code>
              <span className="muted">{t.description ?? ''}</span>
              <button
                type="button"
                className="btn btn--tiny danger"
                onClick={() => onRemove(t.ip)}
              >×</button>
            </li>
          ))
        )}
      </ul>
    </section>
  );
}
